import { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import { translate } from 'translations';
import { useNavigate } from 'react-router-dom';
import { Button, Icon, Loader, Tooltip } from 'learning-panel-components';
import styles from './programLanguageSelection.module.scss';

const ProgramLanguageSelection: FC<ProgramLanguageSelectionProps> = (props: ProgramLanguageSelectionProps) => {
    const { onSelect, userLanguages, isLoading } = props;
    const languagesSections = [
        {
            name: 'web',
            languages: [
                { name: 'java', displayName: 'Java' },
                { name: 'dotnet', displayName: '.NET' },
                { name: 'python', displayName: 'Python Django' },
                { name: 'php', displayName: 'PHP' },
                { name: 'scala', displayName: 'Scala' },
                { name: 'go', displayName: 'Go' },
                { name: 'ruby', displayName: 'Ruby on Rails' },
                { name: 'nodejs', displayName: 'Node.JS' }
            ]
        },
        {
            name: 'mobile',
            languages: [
                { name: 'ios', displayName: 'iOS' },
                { name: 'android', displayName: 'Android' }
            ]
        }
    ];

    const defaultLangues = {
        web: { java: 1 },
        mobile: { ios: 1 }
    };
    const navigate = useNavigate();

    const [selectedLanguages, setSelectedLanguages] = useState<any>(userLanguages || {});
    const [lastIndexToSection, setLastIndexToSection] = useState<any>({ web: 0, mobile: 0 });

    useEffect(() => {
        const webLastIndex = userLanguages?.web ? Object.keys(userLanguages.web).length : 0;
        const mobileLastIndex = userLanguages?.mobile ? Object.keys(userLanguages.mobile).length : 0;
        setLastIndexToSection({ web: webLastIndex, mobile: mobileLastIndex });
        setSelectedLanguages(userLanguages || {});
    }, [userLanguages]);

    const onSelectLanguage = (language: string, section: string) => {
        let sectionLanguages = selectedLanguages[section];
        if (sectionLanguages && Object.keys(sectionLanguages)?.includes(language)) {
            const deletedValue = sectionLanguages[language];
            delete sectionLanguages[language];
            for (const key in sectionLanguages) {
                if (sectionLanguages[key] > deletedValue) {
                    sectionLanguages[key] -= 1;
                }
            }
            setSelectedLanguages({ ...selectedLanguages, [section]: { ...sectionLanguages } });
            setLastIndexToSection({ ...lastIndexToSection, [section]: lastIndexToSection[section] - 1 });
            return;
        }
        sectionLanguages = { ...sectionLanguages, [language]: lastIndexToSection[section] + 1 };
        setSelectedLanguages({ ...selectedLanguages, [section]: sectionLanguages });
        setLastIndexToSection({ ...lastIndexToSection, [section]: lastIndexToSection[section] + 1 });
    };

    return isLoading ? (
        <div className={styles['program-language-selection']}>
            <Loader />
        </div>
    ) : (
        <div className={styles['program-language-selection']}>
            {
                <div className={styles.titles}>
                    <div>{translate('programLanguageSelectionFirstTitle')}</div>
                    <div className={styles.second}>{translate('programLanguageSelectionSecondTitle')}</div>
                    <div className={styles.third}>{translate('programLanguageSelectionThirdTitle')}</div>
                </div>
            }
            {languagesSections.map(section => (
                <div key={section.name} className={styles.section}>
                    <div className={styles.title}>{translate(section.name)}</div>
                    <div className={styles.languages}>
                        {section.languages.map(language => (
                            <div className={styles['language-wrapper']} key={language.name}>
                                {selectedLanguages[section.name] && selectedLanguages[section.name][language.name] && (
                                    <div className={styles['selected-background']}>
                                        {selectedLanguages[section.name][language.name]}
                                    </div>
                                )}
                                <div
                                    key={language.name}
                                    className={classNames(styles.language, {
                                        [styles['selected-language']]:
                                            selectedLanguages[section.name] &&
                                            selectedLanguages[section.name][language.name]
                                    })}
                                    onClick={() => onSelectLanguage(language.name, section.name)}
                                >
                                    <div title={language.displayName} className={styles.text}>
                                        {language.displayName}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            <div className={styles.bottom}>
                <div className={styles.buttons}>
                    <div className={styles.back}>
                        <div className={styles['back-content']} onClick={(): void => navigate('/app/home')}>
                            <Icon name="back" className={styles['back-icon']} />
                            <div>{translate('Exit')}</div>
                        </div>
                    </div>
                    <div className={styles['button-wrapper']}>
                        <Button
                            text={translate('GetStarted')}
                            type="gradient"
                            onClick={() => onSelect(selectedLanguages)}
                        />
                    </div>
                </div>
                {Object.keys(selectedLanguages).length === 0 && (
                    <div className={styles['decide-later']}>
                        <div onClick={() => onSelect(defaultLangues)}>{translate('decideLater')}</div>
                        <div className={styles['icon-wrapper']}>
                            <Tooltip title={translate('selectionLanguageDefaultMessage')} placement="top-end">
                                <span>
                                    <Icon name="info" className={styles.icon} />
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

interface ProgramLanguageSelectionProps {
    onSelect: Function;

    userLanguages?: any;
    isLoading: boolean;
}

export default ProgramLanguageSelection;
